import { useState, useRef, useEffect } from "react";
import axios from "../axios";
import * as url from "../utils/url";

// Bug923/Bug930: playback state for one hashtag audio or video link code, shared
// by the /a/:code and /v/:code pages and the platform's audio and video views.
// - While `active`, it reads the status once and, only when the server says a
//   conversion is needed (canStart), starts it with one POST — no Listen click.
// - It then polls the read-only status (never POST) every 2.5 s with backoff,
//   paused in hidden tabs, until READY / FAILED, and gives up after 10 minutes
//   ("stalled": the user can check again).
// - Unmounting only stops this page's requests; the server keeps converting.
// - retry() is the explicit Retry (busy / retryable failure); refresh() re-reads
//   the status, e.g. to get a fresh signed URL when the old one has expired.
// Bug930: video uses the same flow by passing VIDEO_LINK_URLS; its pages stay
// inactive until the viewer presses Play.
export const MEDIA_LINK_PHASE = {
  IDLE: "idle",
  CHECKING: "checking",
  PROCESSING: "processing",
  BUSY: "busy",
  READY: "ready",
  FAILED: "failed",
  STALLED: "stalled",
  UNAVAILABLE: "unavailable",
  ERROR: "error",
};

// Module-level so they stay the same objects between renders (effect dependency).
const AUDIO_LINK_URLS = { resolve: url.resolveAudioLinkPublic, start: url.startAudioLinkConversionPublic };
export const VIDEO_LINK_URLS = { resolve: url.resolveVideoLinkPublic, start: url.startVideoLinkConversionPublic };

const POLL_INITIAL_MS = 2500;
const POLL_MAX_MS = 10000;
const POLL_GIVE_UP_MS = 10 * 60 * 1000;

const readLinkState = (response) => {
  if (response && response.data && response.data.data) return response.data.data;
  return null;
};

const useMediaLinkConversion = (code, active, linkUrls = AUDIO_LINK_URLS) => {
  const [phase, setPhase] = useState(active ? MEDIA_LINK_PHASE.CHECKING : MEDIA_LINK_PHASE.IDLE);
  const [linkState, setLinkState] = useState(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(null);
  const actionsRef = useRef({ retry: () => {}, refresh: () => {} });

  useEffect(() => {
    if (!active || !code) return undefined;

    let cancelled = false;
    let pollTimer = null;
    let pollDelay = POLL_INITIAL_MS;
    let pollStartedAt = 0;
    let pollingWanted = false;

    const update = (nextPhase, nextState, nextRetryAfter) => {
      if (cancelled) return;
      if (nextState) setLinkState(nextState);
      setRetryAfterSeconds(nextRetryAfter || null);
      setPhase(nextPhase);
    };

    const stopPolling = () => {
      pollingWanted = false;
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const resetPolling = () => {
      stopPolling();
      pollDelay = POLL_INITIAL_MS;
      pollStartedAt = 0;
    };

    let checkStatus = null;

    const schedulePoll = () => {
      if (cancelled) return;
      pollingWanted = true;
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = null;
      if (!pollStartedAt) pollStartedAt = Date.now();
      if (Date.now() - pollStartedAt > POLL_GIVE_UP_MS) {
        stopPolling();
        update(MEDIA_LINK_PHASE.STALLED);
        return;
      }
      // Hidden tab: resumed by the visibilitychange listener below.
      if (document.hidden) return;
      pollTimer = setTimeout(() => {
        pollTimer = null;
        checkStatus(false);
      }, pollDelay);
      pollDelay = Math.min(Math.round(pollDelay * 1.5), POLL_MAX_MS);
    };

    const handleRequestError = (error, fallbackState) => {
      if (cancelled) return;
      const response = error && error.response;
      const status = response ? response.status : 0;
      if (status === 404) {
        stopPolling();
        update(MEDIA_LINK_PHASE.UNAVAILABLE);
        return;
      }
      const body = response && response.data ? response.data : null;
      const bodyState = body && body.data ? body.data : fallbackState;
      if (status === 429 || status === 503) {
        const retryAfter = Number(response.headers && response.headers["retry-after"]);
        update(MEDIA_LINK_PHASE.BUSY, bodyState, retryAfter > 0 ? retryAfter : null);
        return;
      }
      update(MEDIA_LINK_PHASE.ERROR, bodyState);
    };

    const startConversion = (currentState) => {
      axios
        .post(linkUrls.start(code))
        .then((res) => {
          // Never start again from here: a failure is shown, not retried automatically.
          applyState(readLinkState(res), false);
        })
        .catch((error) => handleRequestError(error, currentState));
    };

    const applyState = (state, mayStart) => {
      if (cancelled) return;
      if (!state) {
        stopPolling();
        update(MEDIA_LINK_PHASE.UNAVAILABLE);
        return;
      }
      if (state.status === "READY" && state.url) {
        stopPolling();
        update(MEDIA_LINK_PHASE.READY, state);
        return;
      }
      if (state.status === "PROCESSING") {
        update(MEDIA_LINK_PHASE.PROCESSING, state);
        schedulePoll();
        return;
      }
      stopPolling();
      if (mayStart && state.canStart) {
        update(MEDIA_LINK_PHASE.CHECKING, state);
        startConversion(state);
        return;
      }
      update(MEDIA_LINK_PHASE.FAILED, state);
    };

    checkStatus = (mayStart) => {
      axios
        .get(linkUrls.resolve(code))
        .then((res) => applyState(readLinkState(res), mayStart))
        .catch((error) => handleRequestError(error, null));
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && pollingWanted && !pollTimer && !cancelled) {
        pollDelay = POLL_INITIAL_MS;
        checkStatus(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    actionsRef.current = {
      retry: () => {
        resetPolling();
        update(MEDIA_LINK_PHASE.CHECKING);
        startConversion(null);
      },
      refresh: () => {
        resetPolling();
        update(MEDIA_LINK_PHASE.CHECKING);
        checkStatus(false);
      },
    };

    // Only this first read may start a conversion.
    update(MEDIA_LINK_PHASE.CHECKING);
    checkStatus(true);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      actionsRef.current = { retry: () => {}, refresh: () => {} };
    };
  }, [code, active, linkUrls]);

  return {
    phase,
    linkState,
    retryAfterSeconds,
    retry: () => actionsRef.current.retry(),
    refresh: () => actionsRef.current.refresh(),
  };
};

export default useMediaLinkConversion;
